import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { NV_PATHS, NvButton, NvCheckIcon, NvCompareIcon, NvDocumentIcon, NvDownloadIcon, NvModal } from "@/components/navigator";
import { safeHttpUrl } from "@/lib/safe-url";
import { cn } from "@/lib/utils";
import { downloadDefinition, type AgentDetail, type DefinitionFormat } from "@/services/navigator/agent-detail-service";
import { recordEvent } from "@/services/navigator/finder-service";

import { NvLucideCloseIcon, NvStarOutlineIcon } from "./detail-icons";
import { DETAIL_GUTTER } from "./detail-parts";
import { SupplierLogo, initialOf } from "./supplier-logo";

// 외부 에이전트 상세 헤더 (설계서 slide 40 · Figma 03-01~05): 브레드크럼 → 왼쪽(공급사 로고 · 이름 · 홈페이지 / 수식어 / 제품명 + Certified / 한 줄 소개 최대 3줄 /
// 주황 CTA 1개 + Agent 정의서 / 회사 소개서 · 저장 · 비교함) + 오른쪽 데모 영상 · 대표 이미지(자동 생성) → 분류 띠 4종 (값이 빈 칩만 숨김).
// 주황 채움 버튼은 'PwC와 함께 도입 문의하기' 1개만 (디자인 요건).

export function finderPathOf(detail: AgentDetail) {
  const params = new URLSearchParams();
  if (detail.breadcrumb.industry) params.set("industry", detail.breadcrumb.industry);
  if (detail.breadcrumb.industry_detail) params.set("industry_detail", detail.breadcrumb.industry_detail);
  const query = params.toString();
  return query ? `${NV_PATHS.agentFinder}?${query}` : NV_PATHS.agentFinder;
}

// compact: 내부 에이전트 상세(Figma 546:778)의 12/14 · #5E626A 경로
export function Breadcrumb({ detail, tail, compact }: { detail: AgentDetail; tail?: string; compact?: boolean }) {
  const sep = <span className="px-[6px]">/</span>;
  return (
    <nav aria-label="경로" className={compact ? "text-[12px] leading-[14px] text-nv-text-secondary" : "text-[13px] leading-[21px] text-nv-text-tertiary"}>
      <Link to={NV_PATHS.agentFinder} className="hover:text-nv-text-primary">
        Agent Finder
      </Link>
      {detail.breadcrumb.label && (
        <>
          {sep}
          <Link to={finderPathOf(detail)} className="hover:text-nv-text-primary">
            {detail.breadcrumb.label}
          </Link>
        </>
      )}
      {sep}
      {tail ? (
        <>
          <Link to={`/agents/${detail.id}`} className="hover:text-nv-text-primary">
            {detail.name}
          </Link>
          {sep}
          <span className="text-nv-text-secondary">{tail}</span>
        </>
      ) : (
        <span className="text-nv-text-secondary">{detail.name}</span>
      )}
    </nav>
  );
}

export { initialOf };

const FORMATS: { value: DefinitionFormat; label: string; ext: string; tone: string }[] = [
  { value: "pdf", label: "PDF", ext: ".pdf", tone: "bg-[#C9302C]" },
  { value: "html", label: "HTML", ext: ".html", tone: "bg-nv-brand" },
  { value: "md", label: "Markdown", ext: ".md", tone: "bg-nv-surface-inverse" },
];

// Agent 정의서 형식 선택 (Figma 203:5861 → ver.7 847:44894, IA 119): 닫기 아이콘 Lucide x 24px
export function DefinitionFormatDialog({ agentId, open, onOpenChange, onError }: {
  agentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onError: (message: string) => void;
}) {
  const [format, setFormat] = useState<DefinitionFormat>("pdf");
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      await downloadDefinition(agentId, format);
      onOpenChange(false);
    } catch {
      onError("정의서를 내려받지 못했어요. 잠시 후 다시 시도해 주세요.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <NvModal
      open={open}
      onOpenChange={onOpenChange}
      title="Agent 정의서 형식 선택"
      width={460}
      closeIcon={<NvLucideCloseIcon size={24} />}
      footer={
        <div className="flex w-full justify-end gap-2 pt-2">
          <NvButton variant="secondary" onClick={() => onOpenChange(false)}>
            취소
          </NvButton>
          <NvButton onClick={download} disabled={busy}>
            다운로드
          </NvButton>
        </div>
      }
    >
      <p className="text-[13px] leading-4 text-nv-text-secondary">내려받을 형식을 선택해 주세요</p>
      <div role="radiogroup" aria-label="정의서 형식" className="flex flex-col gap-4">
        {FORMATS.map((f) => (
          <button
            key={f.value}
            type="button"
            role="radio"
            aria-checked={format === f.value}
            onClick={() => setFormat(f.value)}
            className={cn(
              "flex h-[62px] items-center gap-3 rounded-nv-card border px-4 text-left",
              format === f.value ? "border-nv-brand bg-nv-brand-subtle" : "border-nv-border bg-nv-surface hover:border-nv-border-strong",
            )}
          >
            <span aria-hidden className={cn("h-8 w-8 rounded-nv-pill", f.tone)} />
            <span className={cn("text-[15px] leading-[18px] text-nv-text-primary", format === f.value ? "font-bold" : "font-medium")}>{f.label}</span>
            <span className="ml-auto text-[12px] leading-[14.5px] text-nv-text-secondary">{f.ext}</span>
          </button>
        ))}
      </div>
    </NvModal>
  );
}

// 회사 소개서 · 브로셔: 공급사가 제출한 링크가 있을 때만, 여러 개면 버튼 하나에 개수 뱃지로 묶고 누르면 목록 (설계서 slide 40 1-5)
function BrochureButton({ urls: rawUrls, agentId }: { urls: string[]; agentId: string }) {
  const [open, setOpen] = useState(false);
  const urls = rawUrls.flatMap((url) => safeHttpUrl(url) ?? []); // http · https만 링크로
  if (!urls.length) return null;
  const label = (
    <>
      <NvDownloadIcon size={16} />
      회사 소개서·브로셔 내려받기
      {urls.length > 1 && <span className="ml-1 font-medium">{urls.length}</span>}
    </>
  );
  const cls = "inline-flex h-11 items-center gap-[7px] px-[18px] text-[14px] leading-[22px] text-nv-text-secondary hover:text-nv-text-primary";
  if (urls.length === 1) {
    return (
      <a href={urls[0]} target="_blank" rel="noreferrer" onClick={() => recordEvent("brochure_download", agentId)} className={cls}>
        {label}
      </a>
    );
  }
  return (
    <div className="relative">
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className={cls}>
        {label}
      </button>
      {open && (
        <ul className="absolute left-0 top-11 z-20 min-w-[200px] rounded-nv-card border border-nv-border bg-nv-surface py-1 shadow-nv-float">
          {urls.map((url, i) => (
            <li key={url}>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  recordEvent("brochure_download", agentId, { index: i + 1 });
                  setOpen(false);
                }}
                className="block px-3 py-2 text-[13px] leading-4 text-nv-text-primary hover:bg-nv-bg-page"
              >
                공급사 자료 {i + 1}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface HeaderActions {
  inquiryPath: string;
  onInquiry: () => void;
  onBookmark: () => void;
  onCompare: () => void;
  onDefinition: () => void;
}

// 데모 영상: YouTube · Vimeo는 재생 속도(1x · 2x)를 iframe API로, 영상 파일은 <video>로. 그 밖의 주소는 속도 토글 없이 그대로 보여줘요.
function videoKind(url: string): "youtube" | "vimeo" | "file" | "other" {
  if (/(?:youtube\.com|youtu\.be)\//i.test(url)) return "youtube";
  if (/vimeo\.com\//i.test(url)) return "vimeo";
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) return "file";
  return "other";
}

function embedUrl(url: string, kind: ReturnType<typeof videoKind>) {
  if (kind === "youtube") {
    const id = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/)?.[1];
    return id ? `https://www.youtube.com/embed/${id}?enablejsapi=1&rel=0` : url;
  }
  if (kind === "vimeo") {
    const id = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1];
    return id ? `https://player.vimeo.com/video/${id}?api=1` : url;
  }
  return url;
}

function DemoPanel({ detail }: { detail: AgentDetail }) {
  const supplier = detail.supplier;
  const demoUrl = safeHttpUrl(detail.media.demo_video_url);
  const kind = demoUrl ? videoKind(demoUrl) : null;
  const canSpeed = kind === "youtube" || kind === "vimeo" || kind === "file";
  const [speed, setSpeed] = useState<1 | 2>(1);
  const frame = useRef<HTMLIFrameElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (kind === "file" && video.current) video.current.playbackRate = speed;
    if (kind === "youtube") frame.current?.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "setPlaybackRate", args: [speed, true] }), "*");
    if (kind === "vimeo") frame.current?.contentWindow?.postMessage(JSON.stringify({ method: "setPlaybackRate", value: speed }), "*");
  }, [speed, kind]);

  return (
    <div className="relative flex h-[300px] min-w-0 flex-1 flex-col overflow-hidden rounded-[20px] border border-nv-border bg-[linear-gradient(135deg,#E9ECFB_0%,#F5F7F8_55%,#FDEEE3_100%)] lg:h-[346px]">
      {demoUrl && kind === "file" && <video ref={video} src={demoUrl} controls className="h-full w-full bg-black object-contain" />}
      {demoUrl && kind !== "file" && (
        <iframe ref={frame} title={`${detail.name} 데모 영상`} src={embedUrl(demoUrl, kind ?? "other")} className="h-full w-full" allow="fullscreen; picture-in-picture" />
      )}
      {!demoUrl && (
        // 영상이 없으면 로고(없으면 이니셜)와 제품명으로 대표 이미지를 만들어요 — 영상 미제출 제품도 헤더가 비어 보이지 않게
        <>
          {/* 로고 + 제품명 묶음은 패널 전체의 세로 가운데 (Figma ver.7), 안내 글은 왼쪽 아래 */}
          <div className="flex flex-1 flex-col items-center justify-center gap-[14px]">
            <SupplierLogo name={supplier?.name ?? detail.name} logoUrl={supplier?.logo_url} size={96} round={false} className="shadow-nv-card" />
            <span className="max-w-[80%] text-center text-[17px] leading-5 text-nv-text-secondary">{detail.name}</span>
          </div>
          <span className="absolute bottom-[22px] left-6 text-[13px] leading-4 text-nv-text-tertiary">대표 이미지 (자동 생성)</span>
        </>
      )}
      {demoUrl && canSpeed && (
        <div role="group" aria-label="재생 속도" className="absolute bottom-4 right-6 inline-flex h-7 items-center gap-2 rounded-full border border-nv-border bg-white/90 px-[11px] text-[12px] leading-[14px]">
          {([1, 2] as const).map((s) => (
            <button key={s} type="button" aria-pressed={speed === s} onClick={() => setSpeed(s)} className={cn(speed === s ? "font-bold text-nv-brand-text" : "text-nv-text-tertiary")}>
              {s}x
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ExternalHeader({ detail, actions }: { detail: AgentDetail; actions: HeaderActions }) {
  const supplier = detail.supplier;
  const homepage = safeHttpUrl(supplier?.homepage, true);
  return (
    // 상단 여백 (Figma ver.7 05): GNB → 브레드크럼 32 · 브레드크럼 → 히어로 20 · 히어로 아래 → 분류 띠 40.
    // 오른쪽 데모 패널은 왼쪽 묶음 높이에 맞춰 세로 가운데 (왼쪽이 더 낮으면 패널이 맨 위)
    <section className={cn(DETAIL_GUTTER, "pb-10 pt-8")}>
      <Breadcrumb detail={detail} />
      <div className="mt-5 flex flex-col gap-10 lg:flex-row lg:gap-[24px]">
        <div className="flex min-w-0 flex-col pt-2 lg:w-[556px] lg:shrink-0">
          {supplier && (
            <div className="flex items-center gap-[10px]">
              <SupplierLogo name={supplier.name} logoUrl={supplier.logo_url} size={28} round={false} />
              <span className="text-[14px] leading-[22px] text-nv-text-secondary">{supplier.name}</span>
              {homepage && (
                <a
                  href={homepage}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => recordEvent("homepage_click", detail.id)}
                  className="text-[14px] leading-[22px] text-nv-text-secondary underline underline-offset-2 hover:text-nv-text-primary"
                >
                  {supplier.homepage_label ?? supplier.homepage}
                </a>
              )}
            </div>
          )}
          <div className="mt-8 flex flex-col gap-[6px]">
            {detail.modifier && <span className="text-[17px] leading-[27px] text-nv-text-secondary">{detail.modifier}</span>}
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="break-keep text-[40px] font-normal leading-[46px] text-nv-text-primary xl:text-[52px] xl:leading-[58px]">{detail.name}</h1>
              {detail.is_certified && (
                <span className="inline-flex h-[29px] items-center gap-[3px] rounded-nv-control border border-nv-brand px-3 text-[13px] leading-[21px] text-nv-brand-text">
                  <NvCheckIcon size={13} />
                  PwC Certified
                </span>
              )}
            </div>
          </div>
          {detail.summary && <p className="mt-[22px] line-clamp-3 break-keep text-[18px] leading-[31px] text-nv-text-primary">{detail.summary}</p>}
          <div className="mt-[29px] flex flex-wrap items-center gap-[10px]">
            <NvButton asChild className="h-11 px-[21px] text-[15px]">
              <Link to={actions.inquiryPath} onClick={actions.onInquiry}>
                PwC와 함께 도입 문의하기
              </Link>
            </NvButton>
            <button
              type="button"
              onClick={actions.onDefinition}
              className="inline-flex h-11 items-center gap-[6px] rounded-nv-control border-[1.5px] border-nv-text-primary bg-nv-surface pl-[17px] pr-3 text-[15px] font-medium leading-6 text-nv-text-primary hover:bg-nv-bg-page"
            >
              <NvDocumentIcon size={16} />
              Agent 정의서 보기
            </button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <BrochureButton urls={detail.media.brochure_urls} agentId={detail.id} />
            <button
              type="button"
              onClick={actions.onBookmark}
              aria-pressed={detail.is_bookmarked}
              className={cn(
                "inline-flex h-11 items-center gap-[7px] rounded-nv-control border border-nv-border bg-nv-bg-subtle px-[15px] text-[14px] leading-[22px] hover:border-nv-border-strong",
                detail.is_bookmarked ? "text-nv-brand-text" : "text-nv-text-secondary",
              )}
            >
              <NvStarOutlineIcon filled={detail.is_bookmarked} />
              저장
            </button>
            <button
              type="button"
              onClick={actions.onCompare}
              aria-pressed={detail.in_compare}
              className={cn(
                "inline-flex h-11 items-center gap-[7px] rounded-nv-control border border-nv-border bg-nv-bg-subtle px-[15px] text-[14px] leading-[22px] hover:border-nv-border-strong",
                detail.in_compare ? "text-nv-brand-text" : "text-nv-text-secondary",
              )}
            >
              <NvCompareIcon size={16} />
              {detail.in_compare ? "비교함 담김" : "비교함 담기"}
            </button>
          </div>
        </div>
        <div className="flex min-w-0 flex-1 lg:self-center">
          <DemoPanel detail={detail} />
        </div>
      </div>
    </section>
  );
}

// 분류 띠 4종 고정(산업 및 Value Chain · 에이전트 유형(정의 툴팁) · 도입 형태 · 출시 상태): 값이 빈 칩만 숨기고 띠는 유지해요.
export function ClassificationBar({ detail }: { detail: AgentDetail }) {
  const c = detail.classification;
  const items = [
    { label: "산업 및 Value Chain", value: c.industry },
    { label: "에이전트 유형", value: c.agent_type?.label, tip: c.agent_type?.definition },
    { label: "도입 형태", value: c.deployment?.label, tip: c.deployment?.definition },
    { label: "출시 상태", value: c.release_status },
  ];
  return (
    <div className="border-y border-nv-border">
      <dl className={cn(DETAIL_GUTTER, "grid min-h-[88px] grid-cols-2 gap-x-6 gap-y-4 py-[21px] lg:grid-cols-4 lg:gap-x-[24px]")}>
        {items.map((item) => (
          <div key={item.label} className="flex min-w-0 flex-col">
            {item.value ? (
              <>
                <dt className="text-[13px] leading-[21px] text-nv-text-tertiary">
                  {item.label}
                  {item.tip && (
                    <span title={item.tip} aria-label={`${item.label} 정의: ${item.tip}`} className="ml-1 inline-flex translate-y-[2px] cursor-help align-baseline">
                      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 11v5.5" />
                        <circle cx="12" cy="7.9" r="1.1" fill="currentColor" stroke="none" />
                      </svg>
                    </span>
                  )}
                </dt>
                <dd className="break-keep text-[15px] leading-6 text-nv-text-primary">{item.value}</dd>
              </>
            ) : null}
          </div>
        ))}
      </dl>
    </div>
  );
}
